/* eslint-disable no-shadow */
/* eslint-disable react/no-unused-prop-types */
// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import Switch from 'react-switch';

import Select2, { DataSelect2, EventSelect2 } from 'wdna-select2';
import DatePicker, { DateSelectorConfig } from 'wdna-date-selector';

import { UserDO } from 'wdna-components/lib/types.new';
import GranularitySelector from 'wdna-components/lib/datepicker/GranularitySelector';
import { parseDates, getBoundsOfDatesList, DateObject } from 'wdna-components/lib/helpers/date';
import { messageError } from 'wdna-components/lib/helpers/notification';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { ContextType, FilterContext } from '../../../../../types';
import { company, CompanyDO } from '../../../../management/requests';

const ALLOWED_GRANULARITIES: string[] = ['24h', '7d', '30d'];

interface Props {
  initialState?: any;
  autoRefresh?: boolean;
  requestFunc?: () => Promise<void>;
}

interface State {
  company: number[];
  companyArray: CompanyDO[];

  dates: Date[];
  flatpickrMode: string;
  groupDateTime?: string;

  verified: number;
  autoRefresh: boolean;
}

export default class MainFilter extends React.PureComponent<Props, State> {
  private pid: string;
  private mounted: boolean;
  private userIsURW: boolean;
  private firstRender: boolean;
  private parentFilter: any;

  private timer: NodeJS.Timeout | undefined;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public static defaultProps: Partial<Props> = {
    initialState: {},
  };

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.userIsURW = false;
    this.firstRender = true;

    const { initialState = {}, autoRefresh } = props;

    /* INITIAL FILTER STATE */
    this.state = {
      autoRefresh,
      verified: 0,

      dates: this.initialDates(),
      flatpickrMode: 'range',
      groupDateTime: '24h',

      companyArray: [],
      company: [],

      ...initialState,
    };
  }

  /* React Lifecycle methods */
  public componentDidMount(): void {
    this.mounted = true;
    this.userIsURW = hasLevel('role', ['ADM', 'URW']);

    this.load().catch(() => { /* pass */ });
    this.exportInitialData();
    this.refresh(this.props.autoRefresh || false);

    this.parentFilter = this.context?.parentState?.mainFilter;
  }

  public componentDidUpdate(): void {
    if (this.firstRender || !_.isEqual(this.parentFilter, this.context?.parentState?.mainFilter)) {
      this.parentFilter = this.context?.parentState?.mainFilter;
      this.fetchData().catch(() => { /* pass */ });
    }

    if (this.firstRender) this.firstRender = false;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
    this.refresh(false);
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    const companyFilter: Record<string, any> = {};
    if (!this.userIsURW) {
      const user: UserDO = _.get(window.store.getState(), ['auth', 'user']);
      companyFilter.id = _.defaultTo(_.toNumber(user.access?.companyId), undefined);
      companyFilter.clientId = _.defaultTo(_.toNumber(user.access?.clientId), undefined);
    }

    this.loadingStart();
    const companyArray: CompanyDO[] = await company<CompanyDO[]>('list', undefined, companyFilter) || [];
    if (companyArray) {
      this.setLocalState({
        companyArray,
        company: (_.size(companyArray) === 1) ? [companyArray[0].id] : [],
      });
    }
    this.loadingFinish();
  };

  public hideSidebar = (): void => {
    $('#right-panel').addClass('hidden-functions');
    $('#tab-main-content').removeClass('tab-left-content');
    $('#toggle-func-btn').addClass('transparent sidebar-hidden');
  };

  public fetchData = async (): Promise<void> => {
    const { requestFunc } = this.props;
    if (typeof requestFunc !== 'function') return;

    this.loadingStart();
    try {
      const data: any = await requestFunc();
      this.context?.updateParent({
        update: window.getStr(),
        dashboardData: data
      });
    } catch (error: any) {
      messageError('Error', error.message);
    }
    this.loadingFinish();
    this.hideSidebar();
  };

  public loadingStart = (): void => {
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    window.store.dispatch(finishProcess(this.pid));
  };

  public refresh(create: boolean): void {
    if (this.props.autoRefresh !== undefined) {
      if (create) {
        this.timer = setInterval(() => { location.reload(); }, 600000);
      } else {
        clearInterval(this.timer);
      }
    }
  }

  public handleAutoRefresh = (autoRefresh: boolean): void => {
    this.setLocalState({
      autoRefresh,
    }, () => { this.refresh(autoRefresh); });
  };

  public initialDates = (): Date[] => {
    const today: Date = new Date();
    const firstDayOfMonth: Date = new Date(today.getFullYear(), today.getMonth(), 1);

    return [firstDayOfMonth, today];
  };

  public exportInitialData = (): void => {
    this.setLocalState({}, () => {
      this.exportMainFilter();
    });
  };

  public exportMainFilter = (): void => {
    const mainFilter: any = this.getFilterState();
    this.context?.updateParent({
      mainFilter,
    });
  };

  public getFilterState = (): any => {
    const { state } = this;

    const mainFilter: any = _.cloneDeep(state);

    const { groupDateTime, flatpickrMode, dates } = mainFilter;

    const options: any = {
      granularity: groupDateTime,
      mode: flatpickrMode,
    };

    mainFilter.arrFilterDate = parseDates(dates, options);

    if (flatpickrMode === 'multiple') {
      const boundDates: DateObject[] = getBoundsOfDatesList(mainFilter.arrFilterDate);
      mainFilter.arrFilterDate = boundDates;
    }

    return mainFilter;
  };

  /* Handlers */
  public handleChangeDatePicker = (config: DateSelectorConfig): void => {
    this.setLocalState({
      dates: config.getDates(),
      flatpickrMode: config.getMode(),
    });
  };

  public handleChangeGranularities = (groupDateTime: any): void => {
    this.setLocalState({
      groupDateTime,
    });
  };

  public handleClick = (): void => {
    this.exportMainFilter();
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public handleChangeMultiple = (e: EventSelect2): void => {
    const { id } = e.params.data;
    const { selected } = e.params.data;
    if (selected) {
      this.addValue(parseInt(id, 10));
    } else {
      this.remValue(parseInt(id, 10));
    }
  };

  public addValue = (id: number): void => {
    if (this.isValueSelected(id)) return;
    const { company } = this.state || {};
    const selection: any[] = !company ? [] : company.slice();
    selection.push(id);
    this.setLocalState({
      company: selection.sort((a: any, b: any) => {
        return a - b;
      }),
    });
  };

  public remValue = (id: number): void => {
    if (!this.isValueSelected(id)) return;
    const { company } = this.state || {};
    if (!company) return;
    const selection: any[] = company.filter((itemId: number) => itemId !== id);
    this.setLocalState({
      company: selection,
    });
  };

  public isValueSelected = (id: number): boolean => {
    const { company } = this.state || {};
    if (!company) return false;
    return company.includes(id);
  };

  public render(): JSX.Element {
    const showMainRefresh: boolean = (this.props.autoRefresh !== undefined);
    const { autoRefresh, verified, dates, flatpickrMode, groupDateTime, companyArray, company } = this.state;

    // Disable company select if only 1 or less company are available
    const disCompany: boolean = _.size(companyArray) <= 1;

    return (
      <div className="main-filters">
        {showMainRefresh && (
          <div className="auto-refresh-button">
            <br />
            <label htmlFor="normal-switch" style={{ marginRight: '20px' }}>
              <Switch
                id="auto-refresh-button"
                className="react-switch"
                aria-labelledby="neat-label"
                height={30}
                onColor="#78a329"
                offColor="#5c6875"
                offHandleColor="#444"
                onHandleColor="#b7f93d"
                checked={autoRefresh}
                onChange={this.handleAutoRefresh}
              />
              <span style={{ marginLeft: '10px' }}>Auto Refresh</span>
            </label>
          </div>
        )}
        <GranularitySelector
          active={groupDateTime}
          onChange={this.handleChangeGranularities}
          enabledGranularities={ALLOWED_GRANULARITIES}
        />
        <DatePicker
          icon
          hours={[]}
          dates={dates}
          showChangeModes
          mode={flatpickrMode}
          granularity={groupDateTime}
          iconClass="wdna-ico-icon-calendar"
          handleChanges={this.handleChangeDatePicker}
        />
        <Select2
          multiple
          name="company"
          value={company}
          onSelect={this.handleChangeMultiple}
          onUnselect={this.handleChangeMultiple}
          data={companyArray.map((item: CompanyDO): DataSelect2 => {
            return {
              id: item.id,
              text: item.name,
              title: item.name,
            };
          })}
          options={{ placeholder: 'Select company' }}
          style={{ width: '100%' }}
          disabled={disCompany}
        />
        {this.userIsURW && (
          <div style={{ marginTop: '3px' }}>
            <Select2
              name="verified"
              value={verified}
              onChange={this.handleChange}
              data={[
                { id: 0, text: 'ALL' },
                { id: 1, text: 'VERIFIED' },
              ]}
              options={{}}
              style={{ width: '100%' }}
            />
          </div>
        )}
        <div style={{ marginTop: '1px' }}>
          <button type="button" className="filter-btn btn btn-default" onClick={this.handleClick}>
            <i className="wdna-ico-icon-playsearch" />
            <span>GO</span>
          </button>
        </div>
      </div>
    );
  }
}
